import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PIN_STATUS, PIN_PRIORITY, PIN_CATEGORY, formatDateShort, formatRelative } from "@/lib/utils";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import HealthScore from "@/components/HealthScore";
import { X, ArrowUpRight, ArrowDownRight, Minus, ExternalLink, AlertOctagon, Flame } from "lucide-react";

const SEV_COLOR = {
  critical: "#ef4444",
  high: "#f97316",
  medium: "#fbbf24",
  low: "#9899a5",
  info: "#9899a5",
};

export default function HealthDetailsModal({ projectId, projectName, onClose }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/health`).then((r) => setData(r.data));
  }, [projectId]);

  if (!projectId) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="fop-surface-2 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="health-modal"
      >
        <div className="flex items-start gap-4 px-6 py-5 border-b border-[var(--fop-border)]">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)] mb-1">
              Coordination Health
            </div>
            <h2 className="font-display text-2xl text-[var(--fop-text)] truncate">{projectName}</h2>
          </div>
          {data && <HealthScore health={data} size="lg" />}
          <button onClick={onClose} className="btn btn-ghost p-1.5" data-testid="health-modal-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {!data ? (
            <div className="text-[var(--fop-text-3)] font-mono text-xs">Computing…</div>
          ) : (
            <>
              {/* Sparkline / history */}
              <Section title="14-day trend">
                <Sparkline history={data.history || []} color={data.status?.color || "#9899a5"} />
              </Section>

              {/* Why the score */}
              <Section title="Why this score">
                {data.reasons.length === 0 ? (
                  <div className="text-sm text-[var(--fop-text-2)]">
                    No significant operational pressure detected. Project is well-coordinated.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {data.reasons.map((r) => (
                      <li
                        key={r.code}
                        className="flex items-start gap-3 px-3 py-2.5 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)]"
                        data-testid={`health-reason-${r.code}`}
                      >
                        <div
                          className="w-1 self-stretch rounded-full shrink-0"
                          style={{ background: SEV_COLOR[r.severity] }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span
                              className="font-mono text-[10px] uppercase tracking-wider font-semibold"
                              style={{ color: SEV_COLOR[r.severity] }}
                            >
                              {r.severity}
                            </span>
                            <span className="font-mono text-[10px] text-[var(--fop-text-4)] uppercase tracking-wider">
                              {r.code.replace(/_/g, " ")}
                            </span>
                          </div>
                          <div className="text-sm text-[var(--fop-text)] leading-snug">{r.label}</div>
                          {r.detail && (
                            <div className="text-[11px] text-[var(--fop-text-3)] mt-0.5">{r.detail}</div>
                          )}
                        </div>
                        <div className="font-mono text-sm font-bold text-[var(--st-escalated)] shrink-0">
                          −{r.penalty}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Hot sheets */}
              {data.hot_sheets?.length > 0 && (
                <Section title="Issue clustering on sheets" icon={Flame}>
                  <div className="space-y-1.5">
                    {data.hot_sheets.map((hs) => (
                      <button
                        key={hs.sheet_id}
                        onClick={() => { onClose(); navigate(`/sheet/${hs.sheet_id}`); }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-md border border-[var(--fop-border)] hover:border-[var(--fop-primary)] transition-colors group text-left"
                        data-testid={`health-hot-sheet-${hs.sheet_number}`}
                      >
                        <span className="font-mono font-bold text-sm text-[var(--fop-text)] w-16 group-hover:text-[var(--fop-primary)]">
                          {hs.sheet_number}
                        </span>
                        <span className="flex-1 min-w-0 text-sm text-[var(--fop-text-2)] truncate">{hs.sheet_title}</span>
                        <span className="pill st-open">{hs.open_count} open</span>
                        <ExternalLink size={12} className="text-[var(--fop-text-3)]" />
                      </button>
                    ))}
                  </div>
                </Section>
              )}

              {/* Related items */}
              {data.related?.length > 0 && (
                <Section title="Top contributing issues" icon={AlertOctagon}>
                  <ul className="divide-y divide-[var(--fop-border)]">
                    {data.related.map((r) => {
                      const cat = PIN_CATEGORY[r.category] || {};
                      return (
                        <li key={r.id}>
                          <button
                            onClick={() => { onClose(); navigate(`/sheet/${r.sheet_id}`); }}
                            className="w-full flex items-center gap-3 px-1 py-2 hover:bg-[var(--fop-surface)] rounded-md text-left"
                            data-testid={`health-related-${r.id}`}
                          >
                            <span className="w-1 h-6 rounded-full" style={{ background: cat.color }} />
                            <span className="flex-1 min-w-0 text-sm text-[var(--fop-text)] truncate">{r.title}</span>
                            <StatusBadge status={r.status} />
                            <PriorityBadge priority={r.priority} />
                            {r.due_date && (
                              <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] shrink-0">
                                {formatDateShort(r.due_date)}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        {Icon && <Icon size={12} className="text-[var(--fop-primary)]" />}
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Sparkline({ history, color }) {
  if (!history || history.length < 2) {
    return <div className="text-xs text-[var(--fop-text-3)]">Not enough data yet.</div>;
  }
  const w = 600, h = 80, padX = 8, padY = 12;
  const xs = history.map((_, i) => padX + (i * (w - padX * 2)) / (history.length - 1));
  const min = Math.min(...history.map((p) => p.score), 0);
  const max = Math.max(...history.map((p) => p.score), 100);
  const range = max - min || 1;
  const ys = history.map((p) => padY + (1 - (p.score - min) / range) * (h - padY * 2));
  const points = xs.map((x, i) => `${x},${ys[i]}`).join(" ");
  const area = `${xs[0]},${h - padY} ${points} ${xs[xs.length - 1]},${h - padY}`;
  return (
    <div className="fop-surface p-3 rounded-md">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" preserveAspectRatio="none">
        <defs>
          <linearGradient id="hg" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#hg)" />
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
        {xs.map((x, i) => (
          <circle key={i} cx={x} cy={ys[i]} r={i === xs.length - 1 ? 3 : 1.5} fill={color} />
        ))}
      </svg>
      <div className="flex justify-between text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] mt-1">
        <span>{history[0]?.date}</span>
        <span>today · {history[history.length - 1]?.score}</span>
      </div>
    </div>
  );
}
